/**
 * FlahaSOIL v2 — standards method library.
 *
 * Short method cards describing published standards FlahaSOIL applies,
 * with clear "Used when" guidance for agronomists. Includes the project
 * SoilTestLevel evidence standard.
 */
import { Box, Chip, Stack, Typography } from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import OpacityIcon from "@mui/icons-material/Opacity";
import ScienceIcon from "@mui/icons-material/Science";
import StraightenIcon from "@mui/icons-material/Straighten";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import type { ReactNode } from "react";
import { SOIL_TEST_LEVEL_STANDARD } from "@flaha/shared-types";

import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";

interface Standard {
	title: string;
	source: string;
	description: string;
	/** Where the standard is applied in the product. */
	appliedIn: string;
	/** When an agronomist should care about this method. */
	usedWhen: string;
	icon: ReactNode;
}

const STANDARDS: Standard[] = [
	{
		title: "Soil hydraulic properties",
		source: "Saxton & Rawls (2006)",
		description:
			"Pedotransfer functions estimate field capacity, wilting point, and plant-available water from texture and organic matter when a lab moisture-release curve is not available.",
		appliedIn: "Soil physics results, water-retention curve, irrigation planning context",
		usedWhen:
			"You need plant-available water or a retention curve from texture + OM only — treat outputs as model estimates, not lab curves.",
		icon: <OpacityIcon />,
	},
	{
		title: "Texture classification",
		source: "USDA Soil Texture Triangle",
		description:
			"Twelve texture classes derived from sand, silt, and clay percentages. Classification feeds interpretation and physics summaries.",
		appliedIn: "Physics results, texture triangle chart, interpretation context",
		usedWhen:
			"You enter or receive particle-size distribution and need a standard USDA class for the sample.",
		icon: <StraightenIcon />,
	},
	{
		title: "Salinity classes",
		source: "FAO Irrigation & Drainage Paper 29",
		description:
			"Electrical conductivity and related thresholds for salinity severity used in agronomic interpretation and salinity–sodicity visuals.",
		appliedIn: "Interpretation salinity risk, salinity & sodicity chart",
		usedWhen:
			"EC (or TDS) is available and you need a severity framing for salts affecting water uptake and crop establishment.",
		icon: <WaterDropIcon />,
	},
	{
		title: "Cation exchange & base saturation",
		source: "Soil Science Society of America conventions",
		description:
			"CEC, base saturation, ESP, and SAR conventions applied in the chemistry engine and structure / cation balance visuals.",
		appliedIn: "Chemistry results, structure triangle, base saturation chart",
		usedWhen:
			"Cation panel (Ca, Mg, K, Na) and CEC are available for nutrient buffering, sodicity, or structure diagnosis.",
		icon: <ScienceIcon />,
	},
];

export function StandardsPage() {
	usePageHeader({
		title: "Scientific standards",
		subtitle: "Methods FlahaSOIL applies — and when they matter",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Standards" },
		],
	});

	return (
		<Box>
			<GlassSurface intensity="subtle" depth="flat" sx={{ p: 2.5, mb: 3 }}>
				<Typography variant="body1" sx={{ color: "workspace.textSecondary", maxWidth: 760 }}>
					FlahaSOIL only applies published, peer-reviewed soil-science methods.
					Each card summarizes the standard, where it is used in the product,
					and when it is relevant to a decision. Formula-level deep dives remain
					in the science documentation; this page is the operator-facing index.
				</Typography>
			</GlassSurface>

			{/* Project evidence contract — SoilTestLevel */}
			<GlassSurface
				component="section"
				aria-labelledby="soil-test-level-standard"
				intensity="standard"
				depth="raised"
				sx={{ p: 2.5, mb: 3 }}
			>
				<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
					<Box
						sx={{
							color: "workspace.green",
							display: "flex",
							p: 0.75,
							borderRadius: 1,
							backgroundColor: "rgba(119, 169, 87, 0.12)",
						}}
						aria-hidden
					>
						<LayersIcon />
					</Box>
					<Box>
						<Typography id="soil-test-level-standard" component="h2" variant="h6">
							Soil test level (evidence standard)
						</Typography>
						<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
							Authoritative project contract — API coverage, wizard panels, reports
						</Typography>
					</Box>
					<Chip label="Project standard" size="small" color="success" variant="outlined" />
				</Stack>
				<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 2, maxWidth: 800 }}>
					<code>SoilTestLevel</code> stratifies expected input completeness so uploads
					can be scored honestly, the UI shows the right form, and reports declare
					their evidence level. Higher levels include all lower-level fields. Extra
					fields beyond the declared level are allowed as supplementary evidence.
				</Typography>
				<Box
					sx={{
						display: "grid",
						gap: 1.5,
						gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
					}}
				>
					{SOIL_TEST_LEVEL_STANDARD.map((lvl) => (
						<Box
							key={lvl.level}
							sx={{
								p: 1.75,
								borderRadius: 1.25,
								border: "1px solid",
								borderColor: "workspace.border",
								backgroundColor: "rgba(12, 16, 14, 0.35)",
							}}
						>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="subtitle1" fontWeight={700}>
									{lvl.label}
								</Typography>
								<Chip
									label={lvl.level}
									size="small"
									variant="outlined"
									sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}
								/>
							</Stack>
							<Typography variant="body2" sx={{ mb: 1, color: "workspace.textSecondary" }}>
								{lvl.summary}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									color: "workspace.green",
									fontWeight: 700,
									letterSpacing: "0.06em",
									mb: 0.5,
								}}
							>
								EXPECTED FIELDS
							</Typography>
							<Typography variant="caption" sx={{ color: "workspace.textMuted", lineHeight: 1.5 }}>
								{lvl.addedFields.map((f) => f.label).join(" · ")}
								{lvl.level !== "PRELIMINARY" ? " (+ all lower levels)" : ""}
							</Typography>
						</Box>
					))}
				</Box>
			</GlassSurface>

			<Box
				sx={{
					display: "grid",
					gap: 2,
					gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
				}}
			>
				{STANDARDS.map((s) => (
					<GlassSurface
						key={s.title}
						component="article"
						intensity="standard"
						depth="raised"
						sx={{ p: 2.5, height: "100%" }}
					>
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ mb: 1.25 }}
						>
							<Box
								sx={{
									color: "workspace.blue",
									display: "flex",
									p: 0.75,
									borderRadius: 1,
									backgroundColor: "rgba(74, 156, 197, 0.12)",
								}}
								aria-hidden
							>
								{s.icon}
							</Box>
							<Typography component="h2" variant="h6" sx={{ flexGrow: 1 }}>
								{s.title}
							</Typography>
							<Chip label="Applied" size="small" color="success" variant="outlined" />
						</Stack>

						<Chip
							label={s.source}
							size="small"
							variant="outlined"
							sx={{
								color: "workspace.blue",
								borderColor: "rgba(74, 156, 197, 0.4)",
								fontWeight: 600,
								mb: 1.5,
							}}
						/>

						<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 2, lineHeight: 1.55 }}>
							{s.description}
						</Typography>

						<Stack spacing={1.25}>
							<Box
								sx={{
									p: 1.25,
									borderRadius: 1.25,
									border: "1px solid",
									borderColor: "rgba(119, 169, 87, 0.28)",
									backgroundColor: "rgba(119, 169, 87, 0.08)",
								}}
							>
								<Typography
									variant="caption"
									sx={{ color: "workspace.green", fontWeight: 700, letterSpacing: "0.06em" }}
								>
									USED WHEN
								</Typography>
								<Typography variant="body2" sx={{ mt: 0.5 }}>
									{s.usedWhen}
								</Typography>
							</Box>
							<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
								<strong>Applied in:</strong> {s.appliedIn}
							</Typography>
						</Stack>
					</GlassSurface>
				))}
			</Box>
		</Box>
	);
}
